/**
 * The upload-registry completion probe, in its own module.
 *
 * Lived inside `adminChunkedUploadDriver.ts` until LSP-19609, when `FileUploadDialog.vue` needed the
 * same fail-closed "does the registry say this upload is assembled?" read for a row it handed to the
 * background that has since been deleted locally. Importing the driver into the dialog for that one
 * function would pull the whole driver (and its module-level lease/session-store singletons) into the
 * dialog's import graph — every dialog spec that partially mocks the lease module broke on the spot.
 * The code below is moved verbatim; the driver re-imports it from here.
 */
import { readUploadFacadeErrorCode } from "@uploads";
import api from "@/api";
import type { FileUploadSession } from "@/db/fileUploadDb";
import { adminUploadTelemetry } from "@/services/uploads/adminUploadTelemetry";
import { uploadFailureLogDetails } from "@/services/uploads/uploadLogRedaction";
import { hasNoServerUploadTarget } from "@/services/uploads/pendingUploadTarget";

/**
 * LSP-19090: the only two statuses that PROVE the bytes are assembled on S3.
 *
 * `processing` is deliberately absent, and its absence is load-bearing.
 * `deriveUploadSessionStatus` (`apps/contact-api/src/db/models/uploadSession.ts:68`) returns
 * `"processing"` from `processingState` alone, short-circuiting BEFORE it ever looks at
 * `transferState`, and `transitionRecordingUploadSessionProcessing`
 * (`helpers/uploadSessionRegistry.ts:1017`) sets that fact while touching neither `transferState`
 * nor `terminalIntent`. So a `processing` session can still be mid-transfer, and retiring it would
 * delete the local chunks for a file S3 does not have. It is also self-resolving: a session that is
 * genuinely processing becomes `ready`, and the next completion attempt probes again and retires
 * then. "Not yet" must stay a retry, never a success (PR #4598 review).
 *
 * `cancelled` is absent for the opposite reason — its object IS assembled, but the session was
 * discarded on purpose and reporting it complete would resurrect a file the user cancelled.
 */
const ASSEMBLED_UPLOAD_REGISTRY_STATUSES: ReadonlySet<string> = new Set(["sealed", "ready"]);

/**
 * Every "no" this reconcile can answer, named.
 *
 * Each of these was an unlogged `false` — five bare returns and one returned boolean expression. The
 * refusal they decline to examine is then re-thrown, classified retryable, and the row goes back to
 * `pending`, so from the logs the upload simply stops with no record of WHICH condition declined it.
 * Reconstructing that from a customer's trace took a full session of archaeology: the `wrong-family`
 * branch below returns before the only log this function had, and it was the exit taken by every one
 * of the fifteen staging runs examined, which is why two minutes of client logs read as silence.
 *
 * This is the `CLAUDE.md` → "Diagnostic logging when fixing silent failures" rule: one line per
 * fail-condition, naming the condition and the state it read, plus `uploadId` so a record can be tied
 * to the row that stalled.
 *
 * Reported through `adminUploadTelemetry` rather than a bare console call: that boundary bounds
 * oversized strings and refuses to walk an arbitrary object graph into the sink, hands Datadog
 * structured attributes instead of a forwarded console record — which is what makes a decline
 * queryable rather than merely present — and already swallows sink failures.
 *
 * It stays cheap because it only runs on a completion refusal, which is already the rare path.
 */
function declineRegistryReconcile(
	reason: "wrong-family" | "no-registry-id" | "unreadable-projection" | "identity-mismatch" | "status-not-assembled" | "probe-failed",
	context: Record<string, unknown>,
	// LSP-19325: a probe that FAILED can still carry a verdict. The facade answering `UPLOAD_NOT_FOUND`
	// is the second confirmation that the target is gone, which the caller must be able to tell apart
	// from a probe that was merely unreadable — so a decline returns which of the two it reached. The
	// telemetry record's shape is a pinned contract (`lsp19088RecoveryDiagnostics.breakit.spec.ts`
	// asserts it exactly), so the verdict is deliberately NOT added to it; the console warn at the
	// probe-failed site carries `reconciliation` for the console channel instead.
	verdict: Exclude<UploadRegistryReconciliation, "assembled"> = "unresolved"
): Exclude<UploadRegistryReconciliation, "assembled"> {
	adminUploadTelemetry.logInfo("recording.fileUpload.registryReconcileDeclined", { reason, ...context });
	return verdict;
}

/**
 * Did the server refuse our completion because it ALREADY HAS the file?
 *
 * A completion refusal cannot answer that on its own. The `/uploads` facade emits at least a dozen
 * distinct conflicts on `POST /contact/uploads/:id/complete`, several of them reachable with the
 * object fully assembled — a re-submitted manifest that no longer matches the reservation, a
 * `processing` session (whose registry facts are literally `transferState: "sealed"`), a
 * reconciliation refusal thrown *after* the server has already HEAD-proved the object exists. None of them
 * carries an "assembled" field in the body, and the error codes are shared with genuinely
 * non-assembled cases. So the response is not evidence, and the client has to go and ask.
 *
 * `GET /contact/uploads/:id` is the one read that answers for every state: it is the only ungated
 * endpoint on the facade — no lifecycle refusal, authorization only — so it still replies for the
 * `processing`/`cancelled` statuses on which `/parts` itself 409s. `adminSmallFileUploadLane`
 * already reconciles its own lane against this same read.
 *
 * Without this, a completion refusal on an assembled upload became `failureKind: retry` forever:
 * resume → 409 → classified retryable → pending → resume. The bytes were safe on the server the
 * whole time while the tray promised "We'll keep retrying automatically." and the media list stayed
 * empty, and only a page reload broke the loop (a reload re-mints the attempt, which is why it
 * worked). Observed on release/99 by QA over a 48-sample window with `terminal=false`.
 *
 * Fails closed: any doubt at all — wrong family, no registry id, an unreadable probe, a status this
 * does not recognise — answers `"unresolved"` and the original refusal stands. Reporting an upload
 * complete that is not costs the customer their file, because contact-admin deletes the local chunks
 * on completion.
 *
 * LSP-19325 added the third answer. This read refuses with `UPLOAD_NOT_FOUND` for exactly two
 * reasons — the registry row is absent/soft-deleted, or the caller cannot see its target at all
 * (`findAuthorizedSession` in the uploads controller) — and R-2 has narrowed that code to mean only
 * "no upload matching this identity exists". Collapsing that into the same `"unresolved"` as an
 * unreadable probe is what left the caller re-sending a dead target forever; it is reported
 * separately as `"absent"` so the caller can stop promising a retry that cannot work. It is still
 * never read as evidence of completion.
 */
export type UploadRegistryReconciliation = "assembled" | "unresolved" | "absent";

/**
 * Exported for LSP-19609: the dialog's deferred-row follow-up asks the same question when a row it
 * handed to the background has since been DELETED locally — Admin's completion path removes the row
 * and its chunks in the same fenced write that marks it complete, so "missing" is only ever read as
 * success on this registry's word. Same fail-closed contract: anything but `"assembled"` is not proof.
 */
export async function readUploadRegistryForCompletionRefusal(
	target: Pick<FileUploadSession, "uploadId" | "endpointFamily" | "registryId">
): Promise<UploadRegistryReconciliation> {
	// A legacy-family row has no registry to ask, so this reconcile can never help it. Expected, not a
	// fault — but it is the exit that accounted for the silence above, so it is logged rather than
	// inferred. Reported as the RAW shape, never folded into "legacy": the field is optional and
	// "legacy" is a real value, so collapsing absent/corrupt into it would hide exactly the persistence
	// regression this line should catch.
	const endpointFamily = target.endpointFamily;
	if (endpointFamily !== "uploads") {
		return declineRegistryReconcile("wrong-family", {
			uploadId: target.uploadId,
			endpointFamily:
				typeof endpointFamily === "string" ? endpointFamily : endpointFamily === undefined ? "absent" : typeof endpointFamily
		});
	}
	const registryId = target.registryId ?? target.uploadId;
	// break-it round 1: `.trim()`, not `length === 0`. A whitespace-only identity passed the old blank
	// check, so the probe asked the facade about `"   "`, got the not-found every malformed id gets,
	// and that answer became the second confirmation — an "absent" verdict about a subject that is not
	// the upload this session holds. This is the same rule the `id !== registryId` identity gate below
	// already enforces for the assembled verdict, and it is a strict tightening: a blank-ish id could
	// never have satisfied that gate either, so the assembled path is unchanged.
	// LSP-21977: a `pending-target:` placeholder is not a registry id — the API 404s it before any lookup.
	if (typeof registryId !== "string" || registryId.trim().length === 0 || hasNoServerUploadTarget(target)) {
		return declineRegistryReconcile("no-registry-id", { uploadId: target.uploadId });
	}
	try {
		const projection = await api.getUploadsUpload(registryId);
		if (!projection || typeof projection !== "object" || Array.isArray(projection)) {
			// `Array.isArray` is load-bearing: an array satisfies `typeof x === "object"`, so a JSON list
			// body used to fall through to the identity check and be reported as a verdict about the WRONG
			// upload, for a body that is not a projection at all.
			return declineRegistryReconcile("unreadable-projection", {
				uploadId: target.uploadId,
				registryId,
				projectionType: Array.isArray(projection) ? "array" : typeof projection
			});
		}
		const { id, status } = projection as { id?: unknown; status?: unknown };
		// The projection must identify the upload we actually asked about. Without this, any 200 body
		// carrying a `status` string — a cached or mismatched registry entry, a proxy error page — is
		// taken as proof and costs the customer their local chunks. `adminSmallFileUploadLane`'s
		// `isExactCurrentReadyReceipt` pins the same identity before trusting its own receipt.
		if (id !== registryId) {
			// The mismatched id is the whole point of the diagnostic — a cached or proxied body is
			// indistinguishable from a correct one without it.
			return declineRegistryReconcile("identity-mismatch", {
				uploadId: target.uploadId,
				registryId,
				projectionId: typeof id === "string" ? id : typeof id
			});
		}
		if (typeof status !== "string" || !ASSEMBLED_UPLOAD_REGISTRY_STATUSES.has(status)) {
			// `processing` lands here on purpose and self-resolves on a later attempt (see the constant's
			// comment); logging it is what makes "not yet" distinguishable from "never" in a trace.
			return declineRegistryReconcile("status-not-assembled", {
				uploadId: target.uploadId,
				registryId,
				status: typeof status === "string" ? status : typeof status
			});
		}
		return "assembled";
	} catch (probeError) {
		// Silent-failure rule (CLAUDE.md "Diagnostic logging when fixing silent failures"): this warn
		// was the ONLY trace of the LSP-19325 loop, and it named neither the probe's verdict nor which
		// refusal produced it, so "did the server say gone, or was the read merely unreadable?" had to
		// be reconstructed from indirect signals. Name the verdict on every failure class.
		const absent = readUploadFacadeErrorCode(probeError) === "UPLOAD_NOT_FOUND";
		console.warn("[Upload] Could not read the upload registry to reconcile a completion refusal", {
			registryId,
			reconciliation: absent ? "absent" : "unresolved",
			...uploadFailureLogDetails(probeError)
		});
		// Also report the rejected request in the same vocabulary as the other declines: before this it
		// was the only decline invisible to the query that finds the other five. The warn above keeps the
		// error detail for the console channel; this makes the path countable alongside its siblings and
		// carries enough detail to tell a 404 from a 5xx from an offline failure — and, via the verdict,
		// a gone target from a probe that merely could not be read.
		return declineRegistryReconcile(
			"probe-failed",
			{ uploadId: target.uploadId, registryId, ...uploadFailureLogDetails(probeError) },
			absent ? "absent" : "unresolved"
		);
	}
}
